import { createModel } from "@rematch/core";
import ICommonState from "../interfaces/states/ICommonState";
import { IInitializePayload } from "../interfaces/payloads/commonReducers";
import { getConfigAsync } from "../../backend/configuration/ConfigurationProvider";
import { AxiosInstance } from "axios";
import { IExternalConfiguration } from "src/backend/interfaces/configuration/IExternalConfiguration";
import { IBaseConfiguration } from "src/backend/interfaces/configuration/IBaseConfiguration";
import { clearStoredApiToken } from "src/backend/configuration/authentication";
import i18n from "src/i18n";
import IMfilesValueListItem from "src/backend/interfaces/mfiles/IMfilesValueListItem";
import ganttDB from "../../backend/storage/database";
import { Dispatch } from "../store";
import { availableLanguages } from "src/backend/constants/common";

export default createModel()({
  state: {
    isAuthenticated: false,
    isOauth2Enabled: false,
    loginError: null,
    loadingConfiguration: false,
    configuration: null,
    httpClient: null,
    language: i18n.language,
    languages: availableLanguages,
  } as ICommonState,
  reducers: {
    _initialize: (state: ICommonState, payload: IInitializePayload): ICommonState => {
      return {
        ...state,
        isAuthenticated: true,
        configuration: payload.configuration,
        httpClient: payload.httpClient,
        languages: payload.languages,
      };
    },
    login: (state: ICommonState): ICommonState => {
      return { ...state, isAuthenticated: true, loadingConfiguration: true, loginError: null };
    },
    loginFailed: (state: ICommonState, loginError: string): ICommonState => {
      return { ...state, isAuthenticated: false, loadingConfiguration: false, loginError: loginError };
    },
    changeLanguage: (state: ICommonState, language: string): ICommonState => {
      return { ...state, language: language };
    },
    showLoadingSpinner: (state: ICommonState, showLoadingSpinner: boolean): ICommonState => {
      return { ...state, loadingConfiguration: showLoadingSpinner };
    },
    useOauth2: (state: ICommonState, isOauth2Enabled: boolean): ICommonState => {
      return { ...state, isOauth2Enabled: isOauth2Enabled };
    },
    setValueListItems: (state: ICommonState, valueListId: number, valueListItems: IMfilesValueListItem[]): ICommonState => {
      return {
        ...state,
        configuration: {
          ...state.configuration,
          valueListItems: {
            ...state.configuration.valueListItems,
            [valueListId] : valueListItems,
          }
        }
      }
    },
  },
  effects: (dispatch0) => ({
    initializeModelsAsync: async (payload: {
      httpClient: AxiosInstance;
      externalConfig: IExternalConfiguration;
      baseConfiguration: IBaseConfiguration;
    }) => {
      const dispatch = dispatch0 as Dispatch;
      const languages = payload?.baseConfiguration?.uiLanguages ?? availableLanguages;
      await dispatch.common.login();
      const configuration = await getConfigAsync(payload.httpClient, payload.externalConfig, payload.baseConfiguration);
      await dispatch.common._initialize({ configuration, httpClient: payload.httpClient, languages: languages });
      await dispatch.filters.initializeFilters({
        viewsCount: configuration.viewConfigurations.length,
      });
      await Promise.all([dispatch.filters.loadPanelFiltersAsync(null), dispatch.UI.loadDisplaySettingsAsync(null)]);
      //Show screen after display settings have been loaded
      await dispatch.common.showLoadingSpinner(false);
      await dispatch.items.fetchItemsAsync(true);
    },
    logout() {
      ganttDB.clearMetadata();
      clearStoredApiToken();
      window.location.reload();
    },
  }),
});
